import { StorageItemKeyEnum } from '../enums/StorageItemKeyEnum';

const StorageProvider = {
  localStorage: window.localStorage,
  sessionStorage: window.sessionStorage,
};

const SECRET_KEY = import.meta.env.VITE_APP_CRYPTO_KEY || 'default';
const onChangeCallbacks: Array<
  (key: string, newValue: any, oldValue: any) => void
> = [];

const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder();

/**
 * @desc Get a crypto key from a secret key
 */
async function getCryptoKey(secretKey: string): Promise<CryptoKey> {
  const keyMaterial = await window.crypto.subtle.importKey(
    'raw',
    TEXT_ENCODER.encode(secretKey),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  return await window.crypto.subtle.deriveKey(
    {
      hash: 'SHA-256',
      iterations: 100000,
      name: 'PBKDF2',
      salt: TEXT_ENCODER.encode('some-salt'),
    },
    keyMaterial,
    { length: 256, name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * @desc Encrypt data using AES-GCM
 */
async function encryptData(
  data: string,
  key: CryptoKey
): Promise<{ data: number[]; iv: number[] }> {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await window.crypto.subtle.encrypt(
    {
      iv: iv,
      name: 'AES-GCM',
    },
    key,
    TEXT_ENCODER.encode(data)
  );
  return {
    data: Array.from(new Uint8Array(encrypted)),
    iv: Array.from(iv),
  };
}

/**
 * @desc Decrypt data using AES-GCM
 */
async function decryptData(
  encryptedData: { data: number[]; iv: number[] },
  key: CryptoKey
): Promise<string> {
  const iv = new Uint8Array(encryptedData.iv);
  const encryptedBytes = new Uint8Array(encryptedData.data);
  const decrypted = await window.crypto.subtle.decrypt(
    {
      iv: iv,
      name: 'AES-GCM',
    },
    key,
    encryptedBytes
  );
  return TEXT_DECODER.decode(decrypted);
}

/**
 * Registers a callback to be invoked on storage change.
 * @param callback The callback to register.
 * @returns Function to remove the callback.
 */
export function onStorageChange(
  callback: (key: string, newValue: any, oldValue: any) => void
): () => void {
  onChangeCallbacks.push(callback);

  // Returns a function to remove the callback
  return () => {
    const index = onChangeCallbacks.indexOf(callback);
    if (index !== -1) {
      onChangeCallbacks.splice(index, 1);
    }
  };
}

/**
 * Invokes all the registered callbacks with the new value.
 * @param key The key that was changed.
 * @param newValue The new value of the item.
 * @param oldValue The old value of the item.
 */
function invokeOnChangeCallbacks(
  key: StorageItemKeyEnum,
  newValue: any,
  oldValue: any
): void {
  onChangeCallbacks.forEach((callback) => callback(key, newValue, oldValue));
}

/**
 * @desc Save data (object or string) to storage
 * @param storageType Either 'localStorage' or 'sessionStorage'
 * @param key The key to store the data under.
 * @param data The data to store (can be an object or a string).
 */
export async function storeStorageItem(
  storageType: 'localStorage' | 'sessionStorage',
  key: StorageItemKeyEnum,
  data: any
): Promise<void> {
  const storage = StorageProvider[storageType];
  const oldValue = await getStorageItem(storageType, key);
  const stringData = typeof data === 'string' ? data : JSON.stringify(data);
  const cryptoKey = await getCryptoKey(SECRET_KEY);
  const encryptedData = await encryptData(stringData, cryptoKey);
  storage.setItem(key, JSON.stringify(encryptedData));
  invokeOnChangeCallbacks(key, data, oldValue);
}

/**
 * @desc Get data from storage
 * @param storageType Either 'localStorage' or 'sessionStorage'
 * @param key The key to retrieve the data from.
 */
export async function getStorageItem(
  storageType: 'localStorage' | 'sessionStorage',
  key: StorageItemKeyEnum
): Promise<any | null> {
  const storage = StorageProvider[storageType];
  const encryptedData = storage.getItem(key);
  if (!encryptedData) {
    return null;
  }

  try {
    const cryptoKey = await getCryptoKey(SECRET_KEY);
    const decryptedData = await decryptData(
      JSON.parse(encryptedData),
      cryptoKey
    );
    try {
      return JSON.parse(decryptedData);
    } catch {
      return decryptedData;
    }
  } catch (err) {
    console.error('Decryption failed', err);
    return null;
  }
}

/**
 * @desc Delete data from storage
 * @param storageType Either 'localStorage' or 'sessionStorage'
 * @param key The key of the data to delete.
 */
export async function deleteStorageItem(
  storageType: 'localStorage' | 'sessionStorage',
  key: StorageItemKeyEnum
): Promise<void> {
  const oldValue = await getStorageItem(storageType, key);
  const storage = StorageProvider[storageType];
  storage.removeItem(key);
  invokeOnChangeCallbacks(key, null, oldValue);
}

/**
 * @desc Clear all data from storage
 */
export function clearStorage(): void {
  window.localStorage.clear();
  window.sessionStorage.clear();
}
